@if (loadingState) {
  <dcp-loading-page [loadingText]="loadingScreenText"></dcp-loading-page>
} @else {
  <div class="udex-grid udex-mx-auto udex-pt-16">
    <div class="row">
      <div class="col-6">
        <h3 class="udex-mb-5">Simulation &amp; Testing Page</h3>
      </div>
      @if (user$ | async; as user) {
        <div class="col-6">
          <udex-badge class="float-end" type="Text" design="Neutral" size="Medium"
            >Logged in as: {{ user.name }} ({{ user.email }} | {{ user.scimUserId }})</udex-badge
          >
        </div>
      }
    </div>
    @if (isAuthorized) {
      <dcp-tab-navigation selectedTab="products"></dcp-tab-navigation>
      <udex-message-strip design="Information" status-type="Icon" hide-close-button class="mb-2 mt-3">
        <strong>Basic:</strong> Shows raw DCP product data reduced to basic fields<br />
        <strong>Full Details:</strong> Shows raw DCP product data<br />
        <strong>CTA & Pricing Preview:</strong> Shows a preview of Product Call To Actions and Pricing (used e.g. in
        PDPs)
      </udex-message-strip>
      <p id="technicalNameHelp" class="udex-mb-8 body--s"></p>
      <div class="d-flex udex-mb-4 row">
        <udex-text-field
          label="Technical Name"
          required="required"
          value-state="Standard"
          aria-described-by="technicalNameHelp"
          id="technicalName"
          class="col-lg-6 col-sm-12 mb-sm-2 col-xs-12"
          [value]="technicalName"
          (input)="onInput($event)">
        </udex-text-field>
        <udex-select-box
          label="Country (for pricing)"
          mode="SingleSelect"
          id="country"
          class="col-lg-6 col-sm-12 mb-sm-2 col-xs-12"
          (selection-change)="onInputCountry($any($event))">
          @for (option of countries$ | async; track option) {
            <udex-select-box-item
              [label]="option.name"
              [value]="option.isocode"
              mode="SingleSelect"
              size="Standard"
              type="None"></udex-select-box-item>
          }
        </udex-select-box>
      </div>
      <div class="d-flex udex-mb-8 row">
        <udex-segmented-button
          accessible-name="Product retrieval type"
          class="col-12"
          size="Large"
          (selection-change)="onInputProductQueryType($any($event))">
          <udex-segmented-button-item icon="sap-icon://detail-less" selected value="BASIC"
            >Basic</udex-segmented-button-item
          >
          <udex-segmented-button-item icon="sap-icon://detail-more" value="FULL"
            >Full Details</udex-segmented-button-item
          >
          <udex-segmented-button-item icon="sap-icon://expense-report" value="PREVIEW"
            >CTA & Pricing Preview</udex-segmented-button-item
          >
        </udex-segmented-button>
      </div>

      <div class="d-flex gap-4 udex-mb-8">
        <udex-button
          class="udex-button--wide"
          (click)="getProduct(technicalName, queryType)"
          size="Large"
          design="Primary">
          Get Product
        </udex-button>
        <udex-button (click)="onReset()" class="udex-button--wide" size="Large" design="Secondary"> Reset </udex-button>
      </div>
      @if (productView === 'tree') {
        <ui5-tree>
          <ng-template #treeItem let-item="item" let-key="key">
            @if (item !== null && item !== undefined) {
              @if (isArray(item)) {
                <ui5-tree-item-custom expanded type="Active">
                  <ui5-text slot="content"
                    ><strong>{{ key }}:</strong></ui5-text
                  >
                  @for (child of item; track child; let i = $index) {
                    <ng-template
                      [ngTemplateOutlet]="treeItem"
                      [ngTemplateOutletContext]="{item: child, key: i}"></ng-template>
                  }
                </ui5-tree-item-custom>
              }
              @if (item && typeof item === 'object' && !isArray(item)) {
                <ui5-tree-item-custom expanded type="Active">
                  <ui5-text slot="content"
                    ><strong>{{ key }}:</strong></ui5-text
                  >
                  @for (k of objectKeys(item); track k) {
                    <ng-template
                      [ngTemplateOutlet]="treeItem"
                      [ngTemplateOutletContext]="{item: item[k], key: k}"></ng-template>
                  }
                </ui5-tree-item-custom>
              }
              @if (isPrimitive(item)) {
                <ui5-tree-item-custom expanded type="Inactive">
                  <ui5-text slot="content" max-lines="1" empty-indicator-mode="On"
                    ><strong>{{ key }}:</strong> {{ item }}</ui5-text
                  >
                </ui5-tree-item-custom>
              }
            }
          </ng-template>
          <ng-template
            [ngTemplateOutlet]="treeItem"
            [ngTemplateOutletContext]="{item: productResult$ | async, key: 'product'}"></ng-template>
        </ui5-tree>
      }

      @if (productView !== 'tree') {
        <div class="product-details" data-testid="product-details-info">
          @if (productDetails$ | async; as productDetails) {
            <udex-tile class="udex-tile">
              <h1 class="no-padding heading__medium--xl">{{ productDetails.productName }}</h1>
              <div class="mt-2">
                @for (category of productDetails.attributes[0].categories; track $index) {
                  <udex-badge class="mr-1" type="Text" design="Neutral" size="Medium">{{ category.id }}</udex-badge>
                }
              </div>
              @for (edition of productDetails.commercial[0].editions; track $index) {
                <div class="row mt-5">
                  <div class="col-8">
                    <h2 class="heading__medium--s">{{ edition.name }}</h2>
                    <ul>
                      <li>Material Number: {{ edition.materialNumber }}</li>
                    </ul>
                    <p>
                      {{ $any(edition).description }}
                    </p>
                  </div>
                  <div class="col-4">
                    <p class="font-weight--bold text-blue-gray">FROM</p>
                    <p class="heading__medium--s mb-3">{{ edition.pricing.formattedValues.listPrice }}</p>
                    @if ($any(edition.pricing).errorCode) {
                      <udex-message-strip design="Negative" status-type="Icon" class="mb-1 mt-1">
                        <strong>{{ $any(edition.pricing).errorCode }}:</strong> {{ $any(edition.pricing).errorMessage }}
                      </udex-message-strip>
                    }
                    @for (cta of edition.transactionValidation.callToActions; track $index) {
                      <udex-button (click)="open(cta.targetUrl)" size="Large" class="mr-1" design="Primary">
                        {{ cta.name }}
                      </udex-button>
                    }
                  </div>
                </div>
              }
            </udex-tile>
          }
        </div>
      }

      <div class="mb-6"></div>
      <div class="mb-6"></div>
      <div class="mb-6"></div>
      <div class="mb-6"></div>
      <div class="mb-6"></div>
    } @else {
      <udex-message-strip design="Warning" status-type="Icon" hide-close-button class="mb-2 mt-3">
        You are not authorized to view this page. Please contact your administrator if you believe this is an error.
      </udex-message-strip>
    }
  </div>
}
